import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const phaseNames: Record<number, string> = {
  1: 'Business Profile', 2: 'Business Intel', 3: 'Brand Guide', 4: 'ICP',
  5: 'Assets', 6: 'Website', 7: 'Blog', 8: 'Social', 9: 'Messaging', 10: 'Activation',
};

export default function AdminClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { impersonate } = useAuthStore();

  useEffect(() => {
    if (!id) return;
    api.getClientDetail(id).then(setData).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-copper-500"></div>
      </div>
    );
  }

  if (!data) return <p>Client not found.</p>;

  const { client, content_items, phase_runs } = data;
  const completedPhases = client.completed_phases || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-charcoal-800">{client.business_name}</h1>
            <p className="text-sm text-gray-500">{client.email} &middot; {client.business_type}</p>
          </div>
        </div>
        <button
          onClick={() => impersonate(client.id)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-copper-500 text-white text-sm font-medium rounded-lg hover:bg-copper-600 transition-colors"
        >
          Login as Client <ArrowRightIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
          <h3 className="font-semibold text-charcoal-700 text-sm uppercase tracking-wider">Business Info</h3>
          <dl className="space-y-2 text-sm">
            {[
              ['Contact', client.contact_name],
              ['Phone', client.phone],
              ['Website', client.website_url],
              ['Location', `${client.city || ''}, ${client.state_province || ''} ${client.country || ''}`],
              ['Model', client.business_model],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <dt className="text-gray-500">{k}</dt>
                <dd className="text-charcoal-700 font-medium">{v || '—'}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
          <h3 className="font-semibold text-charcoal-700 text-sm uppercase tracking-wider">Progress</h3>
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-copper-500 rounded-full h-2.5 transition-all"
                style={{ width: `${(completedPhases.length / 10) * 100}%` }}
              />
            </div>
            <span className="text-sm font-medium text-charcoal-700">{completedPhases.length}/10</span>
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <div
                key={n}
                className={`text-center py-1 rounded text-xs font-medium ${
                  completedPhases.includes(n) ? 'bg-green-100 text-green-700' :
                  n === client.current_phase ? 'bg-copper-100 text-copper-700 ring-1 ring-copper-300' :
                  'bg-gray-100 text-gray-400'
                }`}
                title={phaseNames[n]}
              >
                {n}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
          <h3 className="font-semibold text-charcoal-700 text-sm uppercase tracking-wider">Stats</h3>
          <dl className="space-y-2 text-sm">
            {[
              ['Status', client.status],
              ['Current Phase', `${client.current_phase} — ${phaseNames[client.current_phase]}`],
              ['Google Rating', client.google_rating ? `${client.google_rating} stars` : '—'],
              ['Total Cost', `$${(client.total_cost_usd || 0).toFixed(2)}`],
              ['Created', client.created_at ? new Date(client.created_at).toLocaleDateString() : '—'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <dt className="text-gray-500">{k}</dt>
                <dd className="text-charcoal-700 font-medium">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Content Items */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-charcoal-800">Content Items ({content_items.length})</h2>
        </div>
        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {content_items.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No content generated yet.</p>
          ) : content_items.map((ci: any) => (
            <div key={ci.id} className="px-4 py-3 flex items-center justify-between text-sm">
              <div>
                <p className="font-medium text-charcoal-700">{ci.title || ci.content_key}</p>
                <p className="text-xs text-gray-400">Phase {ci.phase_number} &middot; {ci.content_type}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                ci.status === 'approved' ? 'bg-green-100 text-green-700' :
                ci.status === 'pending_review' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {ci.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Phase Runs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-charcoal-800">Phase Runs ({phase_runs.length})</h2>
        </div>
        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {phase_runs.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No phase runs yet.</p>
          ) : phase_runs.map((pr: any) => (
            <div key={pr.id} className="px-4 py-3 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                {pr.status === 'completed' ? (
                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                ) : pr.status === 'failed' ? (
                  <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
                ) : (
                  <ClockIcon className="h-4 w-4 text-gray-400" />
                )}
                <div>
                  <p className="font-medium text-charcoal-700">Phase {pr.phase_number} — {pr.agent_name}</p>
                  <p className="text-xs text-gray-400">
                    {pr.execution_time_seconds ? `${pr.execution_time_seconds.toFixed(1)}s` : '—'}
                    {pr.cost_usd ? ` · $${pr.cost_usd.toFixed(4)}` : ''}
                  </p>
                </div>
              </div>
              <span className={`text-xs font-medium ${
                pr.status === 'completed' ? 'text-green-600' :
                pr.status === 'failed' ? 'text-red-600' :
                'text-gray-500'
              }`}>
                {pr.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
